import React, { useEffect, useMemo } from 'react';
import { AddButton, DashboardLogo, LeavingGuard, Toasts, ToolbarButton, useModalContext, } from '@fourthwall/components';
import { PlusIcon } from '@fourthwall/icons';
import { preloadImage } from '@fourthwall/utils/dist/preloadImage';
import { routing } from '@utils/routing';
import { EditorPanel } from '../../components/EditorPanel';
import { EditorPanelLoader } from '../../components/EditorPanelLoader';
import { ThemeEditorLayout } from '../../components/ThemeEditorLayout/ThemeEditorLayout';
import { useCurrentShopContext } from '../../hooks/useCurrentShopContext';
import { NavigationTabs } from '../NavigationTabs/NavigationTabs';
import { NavbarToolbar } from './components/NavbarToolbar';
import { ThemePreviewContent } from './components/ThemePreviewContent';
import { useInstallThemeMutation } from './hooks/useInstallThemeMutation';
import { SingleTheme } from './modules/SingleTheme/SingleTheme';
import { ThemeModal } from './modules/ThemeModal';
import { useInstalledThemesContext } from './providers/InstalledThemesProvider';
import * as S from './styled';
import { getPresetImages } from './utils';
export const InstalledThemes = () => {
    const { currentShop } = useCurrentShopContext();
    const { open } = useModalContext();
    const { installThemeMutation } = useInstallThemeMutation();
    const { themeList, libraryThemes, currentTheme, selectedTheme, isLoadingThemeList, refetchThemeList, } = useInstalledThemesContext();
    const presetImages = useMemo(() => getPresetImages(libraryThemes), [libraryThemes]);
    const preloadImages = async () => {
        try {
            await Promise.all(presetImages.map(preloadImage));
        }
        catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        preloadImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetImages]);
    const handleThemeInstall = ({ id, presetHandle, }) => {
        if (installThemeMutation.isLoading)
            return;
        Toasts.notify('Installation in progress. Please wait for a few seconds.', { type: 'info' });
        installThemeMutation.mutate([
            { shop_id: currentShop.id },
            {
                presetHandle: presetHandle || '',
                themeLibraryItemVersionId: id,
                shopThemeRole: 'unpublished',
            },
        ], {
            onSuccess: () => {
                refetchThemeList();
            },
        });
    };
    const handleAddThemeClick = () => open(ThemeModal, { themeList, libraryThemes, onAddTheme: handleThemeInstall });
    const renderThemeList = () => {
        if (isLoadingThemeList) {
            return (React.createElement(S.LoaderContainer, null,
                React.createElement(EditorPanelLoader, null)));
        }
        const [activeTheme, ...restThemes] = themeList;
        return (React.createElement(S.Container, null,
            React.createElement(S.HeaderWrapper, null,
                React.createElement(S.Header, null, "Active theme")),
            activeTheme && (React.createElement(S.ActiveThemeWrapper, null,
                React.createElement(SingleTheme, { key: activeTheme.id, theme: activeTheme }))),
            restThemes.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(S.HeaderWrapper, null,
                    React.createElement(S.Header, null, "Inactive themes"),
                    React.createElement(ToolbarButton, { label: "Add new theme", size: "extraSmall", icon: PlusIcon, onClick: handleAddThemeClick })),
                React.createElement(S.ThemesList, null, restThemes.map((theme) => (React.createElement(SingleTheme, { key: theme.id, theme: theme })))))),
            React.createElement(AddButton, { label: "Create new theme", onClick: handleAddThemeClick })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(LeavingGuard, { when: !!selectedTheme && !!currentTheme && selectedTheme?.id !== currentTheme?.id }),
        React.createElement(ThemeEditorLayout, { logo: React.createElement(DashboardLogo, { name: currentShop.name, to: routing.home }), mobileLogo: React.createElement(DashboardLogo, { to: routing.home }), editorPanel: React.createElement(EditorPanel, null,
                React.createElement(NavigationTabs, null),
                renderThemeList()), navbarContent: React.createElement(NavbarToolbar, { isThemeMain: selectedTheme?.isMain }), content: React.createElement(ThemePreviewContent, { shopUrl: selectedTheme?.previewUrl, isThemeMain: !!selectedTheme?.isMain }) })));
};
